"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.default = void 0;
var _react = _interopRequireDefault(require("react"));
var _propTypes = _interopRequireDefault(require("prop-types"));
var _StyleSheet = _interopRequireDefault(require("react-native-web/dist/cjs/exports/StyleSheet"));
var _Text = _interopRequireDefault(require("react-native-web/dist/cjs/exports/Text"));
var _View = _interopRequireDefault(require("react-native-web/dist/cjs/exports/View"));
var _ThemeProvider = require("../ThemeProvider");
var _utils = require("../utils");
var _Card = require("../Card");
var _StackView = _interopRequireDefault(require("../StackView"));
var _CheckboxButton = _interopRequireWildcard(require("../Checkbox/CheckboxButton"));
var _jsxRuntime = require("react/jsx-runtime");
function _getRequireWildcardCache(e) { if ("function" != typeof WeakMap) return null; var r = new WeakMap(), t = new WeakMap(); return (_getRequireWildcardCache = function (e) { return e ? t : r; })(e); }
function _interopRequireWildcard(e, r) { if (!r && e && e.__esModule) return e; if (null === e || "object" != typeof e && "function" != typeof e) return { default: e }; var t = _getRequireWildcardCache(r); if (t && t.has(e)) return t.get(e); var n = { __proto__: null }, a = Object.defineProperty && Object.getOwnPropertyDescriptor; for (var u in e) if ("default" !== u && {}.hasOwnProperty.call(e, u)) { var i = a ? Object.getOwnPropertyDescriptor(e, u) : null; i && (i.get || i.set) ? Object.defineProperty(n, u, i) : n[u] = e[u]; } return n.default = e, t && t.set(e, n), n; }
function _interopRequireDefault(e) { return e && e.__esModule ? e : { default: e }; }
const [selectProps, selectedSystemPropTypes] = (0, _utils.selectSystemProps)([_utils.a11yProps, _utils.focusHandlerProps, _utils.viewProps]);
const CheckboxCard = /*#__PURE__*/_react.default.forwardRef((_ref, ref) => {
  let {
    tokens,
    variant,
    title,
    children,
    inactive,
    defaultChecked,
    checked,
    name: inputName,
    value,
    id,
    iconId,
    onChange,
    ...rest
  } = _ref;
  const {
    currentValue: isChecked,
    setValue: setIsChecked,
    isControlled
  } = (0, _utils.useInputValue)({
    value: checked,
    initialValue: defaultChecked,
    onChange
  });
  const handleChange = event => {
    if (!inactive) {
      setIsChecked(!isChecked, event);
    }
  };
  const uniqueId = (0, _utils.useUniqueId)('CheckboxCard');
  const inputId = id ?? uniqueId;
  const uniqueIconId = (0, _utils.useUniqueId)('CheckboxIcon');
  const iconCheckboxId = iconId ?? uniqueIconId;
  const getTokens = (0, _ThemeProvider.useThemeTokensCallback)('CheckboxCard', tokens, variant);
  const getCardTokens = cardState => (0, _Card.selectPressableCardTokens)(getTokens(cardState));
  const {
    themeOptions
  } = (0, _ThemeProvider.useTheme)();
  const selectedProps = selectProps({
    accessibilityRole: 'checkbox',
    accessibilityState: {
      checked: isChecked,
      disabled: inactive
    },
    ...rest
  });
  return /*#__PURE__*/(0, _jsxRuntime.jsx)(_Card.PressableCardBase, {
    ref: ref,
    inactive: inactive,
    checked: isChecked,
    tokens: getCardTokens,
    onPress: handleChange,
    ...selectedProps,
    children: cardState => {
      const {
        checkboxSpace,
        contentSpace,
        ...themeTokens
      } = getTokens(cardState);
      const checkboxTokens = (0, _CheckboxButton.selectCheckboxTokens)(themeTokens, 'checkbox');
      const titleTokens = (0, _utils.selectTokens)('Typography', themeTokens);
      const textStyle = (0, _ThemeProvider.applyTextStyles)({
        ...titleTokens,
        themeOptions
      });
      return /*#__PURE__*/(0, _jsxRuntime.jsxs)(_StackView.default, {
        direction: "row",
        space: checkboxSpace,
        children: [/*#__PURE__*/(0, _jsxRuntime.jsx)(_View.default, {
          style: [staticStyles.alignWithText, {
            height: textStyle.lineHeight
          }],
          children: /*#__PURE__*/(0, _jsxRuntime.jsx)(_CheckboxButton.default, {
            tokens: checkboxTokens,
            isControlled: isControlled,
            isChecked: isChecked,
            inactive: inactive,
            defaultChecked: defaultChecked,
            inputId: inputId,
            iconId: iconCheckboxId,
            handleChange: handleChange,
            name: inputName,
            value: value
          })
        }), /*#__PURE__*/(0, _jsxRuntime.jsxs)(_StackView.default, {
          direction: "column",
          space: contentSpace,
          tokens: {
            flexShrink: 1
          },
          children: [title ? /*#__PURE__*/(0, _jsxRuntime.jsx)(_Text.default, {
            style: textStyle,
            children: title
          }) : null, typeof children === 'function' ? children(cardState, textStyle) : children]
        })]
      });
    }
  });
});
CheckboxCard.displayName = 'CheckboxCard';
CheckboxCard.propTypes = {
  ...selectedSystemPropTypes,
  /**
   * Content to be displayed at the top of the card alongside the checkbox
   */
  title: _propTypes.default.string,
  /**
   * Additional content to be displayed below the checkbox.
   */
  children: _propTypes.default.oneOfType([_propTypes.default.node, _propTypes.default.func]),
  /**
   * Use `checked` for controlled checkbox. For uncontrolled checkbox, use the `defaultChecked` prop.
   */
  checked: _propTypes.default.bool,
  /**
   * Use `defaultChecked` to provide the initial value for an uncontrolled checkbox.
   */
  defaultChecked: _propTypes.default.bool,
  /**
   * An optional checkboxdescription.
   */
  description: _propTypes.default.string,
  /**
   * Checkbox card ID.
   */
  id: _propTypes.default.string,
  /**
   * Checkbox icon ID.
   */
  iconId: _propTypes.default.string,
  /**
   * Whether the corresponding input is disabled or active.
   */
  inactive: _propTypes.default.bool,
  /**
   * The label.
   */
  label: _propTypes.default.string,
  /**
   * Associate this checkbox card with a group (set as the name attribute).
   */
  name: _propTypes.default.string,
  /**
   * Whether the underlying input triggered a validation error or not.
   */
  error: _propTypes.default.bool,
  /**
   * The value. Must be unique within the group.
   */
  value: _propTypes.default.oneOfType([_propTypes.default.string, _propTypes.default.number, _propTypes.default.bool]),
  /**
   * Callback called when a controlled checkbox card gets interacted with.
   */
  onChange: _propTypes.default.func,
  /**
   * checkbox card tokens.
   */
  tokens: (0, _utils.getTokensPropType)('CheckboxCard'),
  /**
   * checkbox variant.
   */
  variant: _utils.variantProp.propType
};
var _default = exports.default = CheckboxCard;
const staticStyles = _StyleSheet.default.create({
  alignWithText: {
    justifyContent: 'center'
  }
});